import { AmlAnswerOption, AmlQuestion } from 'api/compliance'

// Multi-select answers are submitted as a single string of canonical option
// texts joined with this separator — the backend matcher splits on
// comma/semicolon, and prior_answer for selects arrives joined with ', '
// (identity.py get_aml_questions), so this round-trips.
export const MULTI_SELECT_SEPARATOR = ', '

export function isSingleSelect(question: AmlQuestion): boolean {
  return (
    question.format === 'single_select' && !!question.answer_options?.length
  )
}

export function isMultiSelect(question: AmlQuestion): boolean {
  return question.format === 'multi_select' && !!question.answer_options?.length
}

// Resolve the form value for a question back to its canonical option(s) —
// a client-side mirror of the backend's _match_answer_options: try the whole
// value as one option first, then treat it as a comma/semicolon list.
export function selectedOptions(
  question: AmlQuestion,
  rawValue: unknown
): AmlAnswerOption[] {
  const options = question.answer_options ?? []
  const value = String(rawValue ?? '').trim()
  if (!value || !options.length) return []

  const whole = options.find(
    option => option.answer.trim().toLowerCase() === value.toLowerCase()
  )
  if (whole) return [whole]

  const tokens = value
    .split(/[;,]/)
    .map(token => token.trim().toLowerCase())
    .filter(Boolean)
  return options.filter(option =>
    tokens.includes(option.answer.trim().toLowerCase())
  )
}

// Toggle one option in a multi-select value, keeping the stored string in
// answer_options display order.
export function toggleMultiOption(
  question: AmlQuestion,
  rawValue: unknown,
  optionAnswer: string,
  checked: boolean
): string {
  const current = selectedOptions(question, rawValue).map(
    option => option.answer
  )
  const next = checked
    ? [...current.filter(answer => answer !== optionAnswer), optionAnswer]
    : current.filter(answer => answer !== optionAnswer)
  return (question.answer_options ?? [])
    .map(option => option.answer)
    .filter(answer => next.includes(answer))
    .join(MULTI_SELECT_SEPARATOR)
}

// True when any chosen option demands a comments value (e.g. 'Other')
export function commentsRequired(
  question: AmlQuestion,
  rawValue: unknown
): boolean {
  return selectedOptions(question, rawValue).some(
    option => option.comments_required
  )
}
